import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  FaClock, 
  FaMapMarkerAlt, 
  FaPhoneAlt, 
  FaWhatsapp, 
  FaCheckCircle, 
  FaTimesCircle,
  FaTooth,
  FaUserMd,
  FaHeart,
  FaCheck,
  FaChevronRight
} from "react-icons/fa";

export default function Contact() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const checkOpenStatus = () => {
      const now = new Date();
      const day = now.getDay();
      const hour = now.getHours();
      const minute = now.getMinutes();
      
      if (day === 0) {
        setIsOpen(false);
        return;
      }
      
      const currentMinutes = hour * 60 + minute;
      const openTime = 9 * 60;
      const closeTime = 19 * 60 + 30;
      
      setIsOpen(currentMinutes >= openTime && currentMinutes < closeTime);
    };
    
    checkOpenStatus();
    const interval = setInterval(checkOpenStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  const validateForm = () => {
    const errors = {};
    if (!name.trim()) errors.name = "Please enter your name";
    else if (name.trim().length < 2) errors.name = "Name must be at least 2 characters";
    
    if (!phone.trim()) errors.phone = "Please enter your phone number";
    else if (!/^[0-9]{10}$/.test(phone.trim())) errors.phone = "Enter valid 10-digit mobile number";
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleWhatsApp = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    
    const now = new Date();
    const formattedDate = now.toLocaleDateString('en-GB');
    const formattedTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    
    const msg = `DENTAL APPOINTMENT BOOKING
================================

PATIENT INFORMATION
------------------
Name: ${name.trim()}
Phone: ${phone.trim()}

APPOINTMENT DETAILS
----------------------------------------
Website Booking Form

Request: ${message.trim() || "No additional message"}

Date: ${formattedDate}
Time: ${formattedTime}

PLEASE CONFIRM THIS APPOINTMENT`;
    
    window.open(`https://wa.me/918590999451?text=${encodeURIComponent(msg)}`, "_blank");
    setIsSubmitting(false);
    
    setName("");
    setPhone("");
    setMessage("");
  };

  const workingHours = [
    { day: "Monday - Saturday", hours: "09:00 AM - 07:30 PM" },
    { day: "Sunday", hours: "Closed", closed: true }
  ];

  const stats = [
    {
      icon: <FaTooth className="text-3xl sm:text-4xl text-teal-500" />,
      value: "Modern Equipment",
      label: "Advanced Technology"
    },
    {
      icon: <FaUserMd className="text-3xl sm:text-4xl text-teal-500" />,
      value: "Experienced Doctors",
      label: "Expert Care"
    },
    {
      icon: <FaHeart className="text-3xl sm:text-4xl text-red-500" />,
      value: "Patient Focused",
      label: "Compassionate Care"
    },
    {
      icon: <FaCheck className="text-3xl sm:text-4xl text-green-500" />,
      value: "Quality Treatment",
      label: "Trusted Care"
    }
  ];

  return (
    <section id="contact" className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-gradient-to-br from-[#F8FBFC] to-white relative overflow-hidden">
      
      <div className="absolute inset-0">
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-40 -right-40 w-80 h-80 bg-teal-100/30 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-teal-50/40 rounded-full blur-3xl"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        
        <div className="text-center mb-12 sm:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-teal-50/80 backdrop-blur-sm px-4 py-2 rounded-full mb-4 border border-teal-100"
          >
            <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
            <span className="text-teal-600 text-xs sm:text-sm font-semibold tracking-wide">GET IN TOUCH</span>
            <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1E293B] mb-2"
          >
            Book Your <span className="bg-gradient-to-r from-[#2CA6B1] to-[#4FD1C5] bg-clip-text text-transparent">Dental Appointment</span>
          </motion.h2>
          
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            whileInView={{ opacity: 1, width: "5rem" }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="h-1 bg-gradient-to-r from-[#2CA6B1] to-[#4FD1C5] mx-auto rounded-full"
          />
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-gray-500 text-sm sm:text-base max-w-2xl mx-auto mt-4"
          >
            Contact Dr. Sainulabid's IQRA Multispeciality Dental Clinic, Athavanad, Malappuram. Call or WhatsApp for consultations, treatments and appointment bookings.
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 md:gap-12">
          
          <div className="space-y-5 sm:space-y-6">
            
            {/* ✅ 1. We Are Open Card - Without CTA Button */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className={`rounded-2xl p-5 sm:p-6 shadow-lg transition-all duration-300 ${
                isOpen 
                  ? 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200' 
                  : 'bg-gradient-to-r from-red-50 to-orange-50 border border-red-200'
              }`}
            >
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  {isOpen ? (
                    <FaCheckCircle className="text-green-500 text-2xl sm:text-3xl animate-pulse" />
                  ) : (
                    <FaTimesCircle className="text-red-500 text-2xl sm:text-3xl" />
                  )}
                  <div>
                    <h3 className={`text-lg sm:text-xl font-bold ${isOpen ? 'text-green-700' : 'text-red-700'}`}>
                      {isOpen ? 'We Are Open' : 'Currently Closed'}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600">
                      {isOpen 
                        ? 'Ready to help with your dental needs' 
                        : 'Visit us during working hours'}
                    </p>
                  </div>
                </div>
                {isOpen ? (
                  <div className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">
                    Open Now
                  </div>
                ) : (
                  <div className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    Closed Now
                  </div>
                )}
              </div>
              
              {isOpen && (
                <div className="mt-3 sm:mt-4">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Opens at 9:00 AM</span>
                    <span>Closes at 7:30 PM</span>
                  </div>
                  <div className="w-full h-1.5 bg-green-100 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: "0%" }}
                      animate={{ width: "70%" }}
                      transition={{ duration: 0.5 }}
                      className="h-full bg-gradient-to-r from-green-500 to-teal-500 rounded-full"
                    />
                  </div>
                </div>
              )}
            </motion.div>

            {/* ✅ 2. Book Appointment Form */}
            <motion.div
              id="appointment-form"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 sm:p-6 shadow-xl border border-white/50"
            >
              <h3 className="text-lg sm:text-xl font-semibold text-[#1E293B] mb-4 sm:mb-5 flex items-center gap-2">
                <FaWhatsapp className="text-green-500" /> Book Appointment
              </h3>
              
              <div className="space-y-4">
                <div>
                  <input
                    type="text"
                    placeholder="Full Name *"
                    className={`w-full p-3 rounded-xl border ${formErrors.name ? 'border-red-500' : 'border-gray-200'} focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20 transition-all bg-white/50 text-sm sm:text-base`}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
                </div>
                
                <div>
                  <input
                    type="tel"
                    placeholder="Mobile Number * (10 digits)"
                    className={`w-full p-3 rounded-xl border ${formErrors.phone ? 'border-red-500' : 'border-gray-200'} focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20 transition-all bg-white/50 text-sm sm:text-base`}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                  />
                  {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
                </div>
                
                <div>
                  <textarea
                    placeholder="Additional Message (Optional)"
                    rows="3"
                    className="w-full p-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20 transition-all resize-none bg-white/50 text-sm sm:text-base"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </div>
                
                <button
                  onClick={handleWhatsApp}
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 shadow-lg flex items-center justify-center gap-2 disabled:opacity-70 text-white text-sm sm:text-base"
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <FaWhatsapp className="text-xl" />
                      Book Appointment on WhatsApp
                    </>
                  )}
                </button>
                
                <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-6 pt-2 text-center">
                  <a
                    href="tel:+918590300370"
                    className="text-xs sm:text-sm text-gray-500 hover:text-teal-600 transition font-medium"
                  >
                    Call: +91 85903 00370
                  </a>
                  <a
                    href="https://wa.me/918590999451"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs sm:text-sm text-gray-500 hover:text-green-600 transition font-medium"
                  >
                    WhatsApp: +91 85909 99451
                  </a>
                </div>
              </div>
            </motion.div>

            {/* ✅ 3. Working Hours */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-white/50"
            >
              <h3 className="text-lg sm:text-xl font-semibold text-[#1E293B] mb-3 sm:mb-4 flex items-center gap-2">
                <FaClock className="text-teal-500" /> Working Hours
              </h3>
              <div className="space-y-2">
                {workingHours.map((schedule, idx) => (
                  <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                    <span className="text-gray-600 text-sm">{schedule.day}</span>
                    <span className={`font-semibold text-sm ${schedule.closed ? 'text-red-500' : 'text-teal-600'}`}>
                      {schedule.hours}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* ✅ 4. Call Us */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-white/50"
            >
              <h3 className="text-lg sm:text-xl font-semibold text-[#1E293B] mb-3 sm:mb-4 flex items-center gap-2">
                <FaPhoneAlt className="text-teal-500" /> Call Us
              </h3>
              <a href="tel:+918590300370" className="text-xl sm:text-2xl font-bold text-teal-600 hover:text-teal-700 transition flex items-center gap-2">
                +91 85903 00370
              </a>
              <p className="text-gray-500 text-xs sm:text-sm mt-2">We're happy to assist you during clinic hours.</p>
            </motion.div>

            {/* ✅ 5. Our Location */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-white/50"
            >
              <h3 className="text-lg sm:text-xl font-semibold text-[#1E293B] mb-3 sm:mb-4 flex items-center gap-2">
                <FaMapMarkerAlt className="text-teal-500" /> Our Location
              </h3>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                Near PMSA Yatheemkhana<br />
                Kattilangadi, Athavanad<br />
                Kerala - 676301
              </p>
              <a
                href="https://maps.app.goo.gl/V4443oiUAViE139R6"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-3 sm:mt-4 text-teal-600 hover:text-teal-700 transition text-sm font-medium"
              >
                <FaMapMarkerAlt className="text-sm" />
                Get Directions
                <FaChevronRight className="text-xs" />
              </a>
            </motion.div>
          </div>

          <div className="space-y-5 sm:space-y-6">
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden shadow-xl"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5906.180498506467!2d76.00761616392809!3d10.898543332183198!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba7b7be6e92b0b3%3A0xa58b62e8456229c4!2sDr%20Sainulabid%27s%20IQRA%20Dental%20Clinic!5e0!3m2!1sen!2sin!4v1781936191912!5m2!1sen!2sin"
                width="100%"
                height="280"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Dr. Sainulabid's IQRA Multispeciality Dental Clinic Location"
                className="w-full h-[280px] md:h-[350px] rounded-2xl"
              />
            </motion.div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {stats.map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl p-4 sm:p-5 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-gray-100 group"
                >
                  <div className="flex flex-col items-center justify-center text-center h-full">
                    <div className="mb-2 sm:mb-3 flex justify-center group-hover:scale-110 transition-transform duration-300">
                      {item.icon}
                    </div>
                    <h4 className="text-xs sm:text-sm font-bold text-[#1E293B] leading-tight">
                      {item.value}
                    </h4>
                    <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
                      {item.label}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}